import * as XLSX from 'xlsx';

/**
 * Export students data to Excel with specific columns
 * @param {Array} students - Array of student objects
 * @param {string} filename - Name of the file to download
 */
export const exportStudentsToExcel = (students, filename = 'students_export.xlsx') => {
  try {
    // Define the exact columns we want to export
    const exportData = students.map(student => {
      const row = {
        'Last Name': student.lastname || '',
        'First Name': student.firstname || '',
        'Middle Name': student.middlename || '',
        'Gender': student.gender || '',
        'Registration ID': '',
      };

      // Add parent1 information if available
      if (student.parent1) {
        row['Parent Last Name'] = student.parent1.lastname || '';
        row['Parent First Name'] = student.parent1.firstname || '';
        row['Parent Phone'] = student.parent1.phone || student.parent1.phone_number || '';
        row['Parent Email'] = student.parent1.email || '';
      } else if (student.parent) {
        // Fallback to 'parent' field if it exists
        row['Parent Last Name'] = student.parent.lastname || '';
        row['Parent First Name'] = student.parent.firstname || '';
        row['Parent Phone'] = student.parent.phone || student.parent.phone_number || '';
        row['Parent Email'] = student.parent.email || '';
      } else {
        // Add empty parent columns if no parent data
        row['Parent Last Name'] = '';
        row['Parent First Name'] = '';
        row['Parent Phone'] = '';
        row['Parent Email'] = '';
      }

      return row;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 20 }, // Last Name
      { wch: 20 }, // First Name
      { wch: 20 }, // Middle Name
      { wch: 12 }, // Gender
      { wch: 18 }, // Registration ID
      { wch: 20 }, // Parent Last Name
      { wch: 20 }, // Parent First Name
      { wch: 18 }, // Parent Phone
      { wch: 30 }, // Parent Email
    ];

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');

    // Download file
    XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Error exporting students to Excel:', error);
    return false;
  }
};

/**
 * Export data to Excel (.xlsx) format
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions (with name, selector, or cell properties)
 * @param {string} filename - Name of the file to download (should end with .xlsx)
 * @param {string} sheetName - Name of the worksheet (optional, defaults to 'Sheet1')
 */
export const exportToExcel = (data, columns, filename = 'export.xlsx', sheetName = 'Sheet1') => {
  try {
    // Extract headers from columns
    const headers = columns
      .filter(col => !col.hidden && col.name && col.name !== 'Actions' && col.name !== 'Options')
      .map(col => col.name);

    // Extract data rows
    const rows = data.map(row => {
      const rowData = {};
      
      columns.forEach(col => {
        // Skip hidden columns and action columns
        if (col.hidden || col.name === 'Actions' || col.name === 'Options') return;
        
        let cellValue = '';
        
        // Handle different column types
        if (col.selector) {
          // If selector is a function, call it
          if (typeof col.selector === 'function') {
            const result = col.selector(row);
            // Extract text from React elements or objects
            cellValue = extractTextValue(result);
          } else {
            // If selector is a string, use it as a key
            cellValue = row[col.selector] || '';
          }
        } else if (col.cell) {
          // If cell is a function, call it and extract text
          const result = col.cell(row);
          cellValue = extractTextValue(result);
        } else if (col.field) {
          // Use field as key
          cellValue = row[col.field] || '';
        }
        
        rowData[col.name] = cellValue;
      });
      
      return rowData;
    });

    // Create worksheet from data
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    
    // Set column widths for better readability
    const columnWidths = headers.map(header => ({
      wch: Math.max(
        header.length + 2,
        Math.max(...rows.map(row => String(row[header] || '').length)) + 2,
        15 // Minimum width
      )
    }));
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

/**
 * Helper function to extract text value from various types
 */
const extractTextValue = (value) => {
  // Handle null/undefined
  if (value === null || value === undefined) return '';
  
  // Handle strings and numbers
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  
  // Handle React elements
  if (typeof value === 'object' && value.props) {
    // Try to extract children text
    if (value.props.children) {
      return extractTextValue(value.props.children);
    }
    // Try to extract from common props
    if (value.props.value) return String(value.props.value);
    if (value.props.title) return String(value.props.title);
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(v => extractTextValue(v)).join(' ');
  }
  
  // Handle objects with toString
  if (typeof value === 'object') {
    // Try common text properties
    if (value.text) return String(value.text);
    if (value.label) return String(value.label);
    if (value.name) return String(value.name);
    
    // Fallback to JSON string for complex objects
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  
  return String(value);
};

/**
 * Export multiple sheets to a single Excel file
 * @param {Array} sheets - Array of sheet objects with { name, data, columns }
 * @param {string} filename - Name of the file to download
 */
export const exportMultipleSheets = (sheets, filename = 'export.xlsx') => {
  try {
    const workbook = XLSX.utils.book_new();
    
    sheets.forEach(({ name, data, columns }) => {
      // Extract headers
      const headers = columns
        .filter(col => !col.hidden && col.name && col.name !== 'Actions' && col.name !== 'Options')
        .map(col => col.name);
      
      // Extract rows
      const rows = data.map(row => {
        const rowData = {};
        columns.forEach(col => {
          if (col.hidden || col.name === 'Actions' || col.name === 'Options') return;
          
          let cellValue = '';
          if (col.selector) {
            cellValue = typeof col.selector === 'function' 
              ? extractTextValue(col.selector(row))
              : row[col.selector] || '';
          } else if (col.cell) {
            cellValue = extractTextValue(col.cell(row));
          } else if (col.field) {
            cellValue = row[col.field] || '';
          }
          rowData[col.name] = cellValue;
        });
        return rowData;
      });
      
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
      
      // Set column widths
      const columnWidths = headers.map(header => ({
        wch: Math.max(header.length + 2, 15)
      }));
      worksheet['!cols'] = columnWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, name);
    });
    
    XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
    return true;
  } catch (error) {
    console.error('Error exporting multiple sheets:', error);
    return false;
  }
};

